import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Kahla v2 header.
 *
 * Three jobs:
 *
 * 1. Publish --header-height and --header-group-height. Horizon's own
 *    header.js sets these by measuring #header-component, which does not
 *    exist once its header is replaced, and the 60px fallback lived in
 *    header.liquid, which no longer renders. Several things read them —
 *    the cart summary's sticky offset, the product page's sticky column,
 *    the media container's viewport offset — and would otherwise slide
 *    under the header.
 *
 * 2. Keep the bag count current. It is rendered server-side, so without
 *    this a quick add from the collection grid leaves the old number in
 *    place until the next page load.
 *
 * 3. Open and close the collapsed nav below 880px.
 */
class KhHeader extends Component {
  #media = window.matchMedia('(max-width: 880px)');
  #resize = null;

  connectedCallback() {
    super.connectedCallback();

    this.#publishHeights();
    this.#resize = new ResizeObserver(() => this.#publishHeights());
    this.#resize.observe(this);
    const group = document.querySelector('#header-group');
    if (group) this.#resize.observe(group);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#media.addEventListener('change', this.#onBreakpoint);
    document.addEventListener('keydown', this.#onKeydown);
    // En el propio elemento, no en `document`: sólo interesan los del nav.
    this.addEventListener('focusout', this.#onDropLeave);
    this.addEventListener('mouseleave', this.#onDropLeave, true);
    this.addEventListener('click', this.#onDropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resize?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#media.removeEventListener('change', this.#onBreakpoint);
    document.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusout', this.#onDropLeave);
    this.removeEventListener('mouseleave', this.#onDropLeave, true);
    this.removeEventListener('click', this.#onDropClick);
    this.#unbindDismiss();
  }

  /**
   * The header's own height, and the whole sticky group's (announcement bar
   * included) — the two offsets the rest of the theme positions against.
   */
  #publishHeights() {
    // On BODY, not documentElement. header.liquid's {% stylesheet %} carries
    // `body { --header-height: 60px }`, and Horizon aggregates every section's
    // stylesheet into ONE global bundle served on every page — so that rule
    // ships even though header.liquid never renders in v2. A custom property
    // set on <html> loses to a rule matching <body> for everything inside it:
    // measured 60px across the whole PDP subtree while <html> carried 76px,
    // which put the sticky buy column 16px too high. Horizon's own writers
    // (header.js, utilities.js) target body for exactly this reason.
    const style = document.body.style;
    style.setProperty('--header-height', `${Math.round(this.getBoundingClientRect().height)}px`);

    const group = document.querySelector('#header-group');
    // #header-group is display:contents so it has no box of its own; sum the
    // sticky wrappers instead.
    const groupHeight = group
      ? [...group.children].reduce((n, el) => n + el.getBoundingClientRect().height, 0)
      : this.getBoundingClientRect().height;
    style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
  }

  /**
   * The cart event is dispatched BEFORE the add completes, carrying a promise
   * that settles once it has. Reading the cart on the event itself returns the
   * pre-add count — measured: the bag stayed at 0 while the cart held 1. So
   * await the promise, exactly as Horizon's own cart icon does.
   *
   * @param {CustomEvent & {promise?: Promise<any>}} event
   */
  #onCartUpdate = (event) => {
    const promise = /** @type {any} */ (event)?.promise;

    if (promise) {
      promise
        .then(({ cart, detail }) => this.#renderCount(cart?.totalQuantity ?? detail?.itemCount ?? 0))
        .catch(() => this.#refetchCount());
      return;
    }

    // pageshow (bfcache) and anything else without a promise.
    this.#refetchCount();
  };

  /**
   * Only a bfcache restore needs a refresh — `pageshow` also fires on every
   * normal load, where the server-rendered count is already correct and a
   * fetch is pure waste.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#refetchCount();
  };

  async #refetchCount() {
    try {
      const res = await fetch(`${Shopify.routes.root}cart.js`);
      if (!res.ok) return;
      const { item_count: count } = await res.json();
      this.#renderCount(count);
    } catch {
      // A failed refresh must not break the header; the count stays as the
      // server rendered it.
    }
  }

  /** @param {number} count */
  #renderCount(count) {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[ref="cartCount"]'));
    if (!el) return;
    el.textContent = count < 100 ? String(count) : '99+';
    // Hidden rather than removed, so 0 -> 1 needs no DOM surgery.
    el.hidden = count === 0;
  }

  /**
   * Opens the cart drawer instead of following the href.
   *
   * @param {Event} event
   */
  openCart = (event) => {
    const drawer = /** @type {any} */ (document.querySelector('#cart-drawer'));
    // No drawer on the page (cart template, or drawer disabled) — let the
    // link do its job.
    if (!drawer?.open) return;

    event.preventDefault();
    drawer.open();
  };

  /**
   * Opens the search overlay instead of following the href.
   *
   * `dialog-component#search-modal` lives in layout/theme.liquid, so it is on
   * every page. Same shape as `openCart`: if it is missing or has no
   * `showDialog`, the link is left to do its job.
   *
   * @param {Event} event
   */
  openSearch = (event) => {
    const modal = /** @type {any} */ (document.querySelector('#search-modal'));
    if (typeof modal?.showDialog !== 'function') return;

    event.preventDefault();
    modal.showDialog();
  };

  /** @param {Event} event */
  toggleMenu = (event) => {
    event.preventDefault();
    this.#setOpen(this.#drawer?.hidden ?? true);
  };

  get #drawer() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.kh-hd__drawer'));
  }

  get #burger() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.kh-hd__burger'));
  }

  /** @param {boolean} open */
  #setOpen(open) {
    const drawer = this.#drawer;
    if (!drawer) return;

    drawer.hidden = !open;
    this.#burger?.setAttribute('aria-expanded', String(open));
    this.#publishHeights();

    if (open) {
      /** @type {HTMLElement | null} */ (drawer.querySelector('a'))?.focus();
      this.#bindDismiss();
    } else {
      this.#unbindDismiss();
    }
  }

  #bindDismiss() {
    document.addEventListener('click', this.#onOutside, true);
  }

  #unbindDismiss() {
    document.removeEventListener('click', this.#onOutside, true);
  }

  /** @param {Event} event */
  #onOutside = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#setOpen(false);
  };

  // Crossing back to desktop must close it, or the drawer stays open behind a
  // burger that no longer exists.
  #onBreakpoint = () => {
    if (!this.#media.matches) this.#setOpen(false);
    this.#publishHeights();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    // El desplegable de escritorio (KSD-68) se abre y se cierra con `:hover`
    // y `:focus-within`, sin estado en JS. Lo único que el CSS no sabe hacer
    // es cerrarlo a petición: quien llegó con el teclado necesita una salida
    // que no sea tabular por las cuatro categorías.
    //
    // Se marca el item y se DEJA EL FOCO en el enlace padre. Antes quitaba
    // el foco con `blur()` y el anillo desaparecía: el teclado perdía de
    // vista dónde estaba.
    const conSubmenu = /** @type {HTMLElement|null} */ (
      document.activeElement instanceof Element ? document.activeElement.closest('.kh-hd__item--sub') : null
    );
    if (conSubmenu && !conSubmenu.classList.contains('is-cerrado')) {
      conSubmenu.classList.add('is-cerrado');
      /** @type {HTMLElement|null} */ (conSubmenu.querySelector(':scope > a'))?.focus();
      return;
    }

    if (this.#drawer?.hidden) return;
    this.#setOpen(false);
    this.#burger?.focus();
  };

  /**
   * La clase `is-cerrado` sólo vale para ese paso por el panel: en cuanto el
   * foco o el ratón se van, se olvida, o el panel no volvería a abrirse.
   * @param {Event} event
   */
  #onDropLeave = (event) => {
    const item = /** @type {HTMLElement|null} */ (
      event.target instanceof Element ? event.target.closest('.kh-hd__item--sub') : null
    );
    if (!item) return;

    // ⚠️ Un `focusout` hacia DENTRO del mismo item no es salir. Al pulsar
    // Escape desde una categoría se devuelve el foco al enlace padre, y ese
    // movimiento dispara un `focusout` en la categoría: sin esta guardia se
    // borraba `is-cerrado` en el acto y el panel volvía a abrirse, porque el
    // foco seguía dentro. Medido.
    if (event.type === 'focusout') {
      const va = /** @type {FocusEvent} */ (event).relatedTarget;
      if (va instanceof Node && item.contains(va)) return;
    }

    item.classList.remove('is-cerrado');
  };

  /**
   * Chrome deja enfocado el enlace que se pulsa, así que tras elegir una
   * categoría el panel se quedaba abierto por encima de la página nueva — y
   * al volver con el botón de atrás, otra vez.
   * @param {Event} event
   */
  #onDropClick = (event) => {
    const item = /** @type {HTMLElement|null} */ (
      event.target instanceof Element ? event.target.closest('.kh-hd__item--sub') : null
    );
    if (item && event.target instanceof Element && event.target.closest('.kh-hd__drop')) {
      item.classList.add('is-cerrado');
    }
  };
}

if (!customElements.get('kh-header')) {
  customElements.define('kh-header', KhHeader);
}

export default KhHeader;
